import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { ArrowRight, MapPin, Sun } from "lucide-react"; // Importar los iconos directamente
import GuateIMG from "../../../components/img/Guatemala.svg";
import {
  formatNumber,
  formatNumberQTZ,
  formatQTZ,
} from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";
import { useConfig } from "../../../context/ConfigContext";
import { logEvent } from "../../../lib/analytics";

const HeroSection = () => {
  const config = useConfig();
  const {
    KWH_MES_PANEL,
    COSTO_ENERGIA_PROM_SINPANEL,
    REDUCE_FACT,
    PLANTAS_SOLARES,
    AHORRO_TOTAL,
    CO2_TOTAL,
    guatemalaRegions,
  } = config;

  const [selectedRegion, setSelectedRegion] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(0);

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
  };

  // Componente de botón personalizado para evitar la dependencia de shadcn/ui
  const CustomButton = ({
    children,
    className,
    onClick,
    icon: IconComponent,
    iconPosition = "left",
  }) => {
    return (
      <button
        onClick={onClick}
        className={`px-4 py-2 rounded-md transition-all duration-300 flex items-center justify-center space-x-2 ${className}`}
      >
        {iconPosition === "left" && IconComponent && (
          <IconComponent size={20} />
        )}
        <span>{children}</span>
        {iconPosition === "right" && IconComponent && (
          <IconComponent size={20} />
        )}
      </button>
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-warm-white via-white to-amber-50 overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0 opacity-10">
        <div
          className={`absolute top-20 right-20 w-32 h-32 bg-primary rounded-full transform transition-all duration-3000 ${animationPhase === 0
            ? "translate-x-0"
            : animationPhase === 1
              ? "translate-x-20"
              : "translate-x-40"
            }`}
        ></div>
        <div
          className={`absolute bottom-40 left-20 w-24 h-24 bg-secondary rounded-full transform transition-all duration-3000 ${animationPhase === 0
            ? "translate-y-0"
            : animationPhase === 1
              ? "-translate-y-10"
              : "-translate-y-20"
            }`}
        ></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-12">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Icon name="Sun" size={16} />
                <span>Energía Solar en Guatemala</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary leading-tight">
                <span className="solar-text-gradient">Energía limpia,</span>
                <br />
                <span className="text-text-primary">ahorros reales</span>
              </h1>

              <p className="text-xl text-text-secondary leading-relaxed max-w-xl">
                Transforma tu hogar con energía solar y reduce tu factura
                eléctrica hasta un {REDUCE_FACT}%. Tecnología de clase mundial
                adaptada al clima guatemalteco.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/calculadora">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                  className="bg-cta-orange hover:bg-cta-orange/90 w-full sm:w-auto"
                  onClick={() => {
                    logEvent(
                      "Navegación",
                      "clic_boton",
                      "Inicio: Calcular Mis Ahorros"
                    );
                  }}
                >
                  Calcular Mis Ahorros
                </Button>
              </Link>

              <Link to="/proyectos">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Eye"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    logEvent(
                      "Navegación",
                      "clic_boton",
                      "Home: Nuestros Proyectos"
                    );
                  }}
                >
                  Nuestros Proyectos
                </Button>
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-6 pt-8 border-t border-border">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-primary">
                  {PLANTAS_SOLARES}+
                </div>
                <div className="text-[10px] sm:text-sm text-text-secondary">
                  Plantas Solares
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-secondary">
                  {formatNumberQTZ(AHORRO_TOTAL)}+
                </div>
                <div className="text-[10px] sm:text-sm text-text-secondary">
                  Ahorros Generados
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-accent">
                  {formatNumber(CO2_TOTAL)} (t)
                </div>
                <div className="text-[10px] sm:text-sm text-text-secondary">CO₂ Reducido</div>
              </div>
            </div>
          </div>

          {/* Right Content - Interactive Map */}
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 font-inter">
            <div className="relative w-full max-w-2xl">
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Potencial Solar
                </h3>

                {/* Guatemala Map Visualization */}
                {/* CAMBIO CLAVE: Usamos 'aspect-[4/3]' para una relación de aspecto 4:3 más alta y 'object-contain' para evitar distorsión */}
                <div className="relative w-full aspect-[4/4] rounded-xl overflow-hidden border border-gray-300">
                  {/* Imagen del mapa de Guatemala */}
                  <img
                    src={GuateIMG}
                    alt="Mapa de Guatemala"
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://placehold.co/800x600/E0F2F7/333333?text=Error+al+cargar+mapa";
                    }}
                  />

                  {/* Region Markers */}
                  {guatemalaRegions?.map((region) => (
                    <button
                      key={region?.id}
                      className={`absolute w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 rounded-full border-2 border-white shadow-lg transition-all duration-300 hover:scale-125 focus:outline-none focus:ring-2 focus:ring-offset-2 ${selectedRegion?.id === region?.id
                        ? "bg-orange-500 scale-125 focus:ring-orange-500"
                        : "bg-green-600 hover:bg-green-700 focus:ring-green-600"
                        }`}
                      style={{
                        left: region?.coordinates?.x,
                        top: region?.coordinates?.y,
                        transform: "translate(-50%, -50%)",
                      }}
                      onClick={() => handleRegionClick(region)}
                      aria-label={`Ver datos solares de ${region?.label}`}
                      title={region?.label}
                    />
                  ))}

                  {/* Sun Animation */}
                  <div
                    className={`absolute top-4 w-10 h-10 bg-yellow-400 rounded-full shadow-lg transform transition-all duration-3000 ${animationPhase === 0
                      ? "left-4"
                      : animationPhase === 1
                        ? "left-1/2 -translate-x-1/2"
                        : "right-4"
                      }`}
                  >
                    <div className="absolute inset-0 bg-yellow-300 rounded-full animate-ping opacity-30"></div>
                    <Sun
                      className="absolute inset-0 m-auto text-yellow-600"
                      size={24}
                    />
                  </div>
                </div>

                {/* Region Info Panel */}
                {selectedRegion && (
                  <div className="mt-6 p-5 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-lg text-gray-800 mb-3">
                      {selectedRegion?.name}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 font-medium">
                          Potencial Solar Mensual:
                        </span>
                        <div className="font-bold text-green-700 text-base">
                          {roundTo(
                            (KWH_MES_PANEL * selectedRegion?.porcentaje) / 100
                          )}{" "}
                          kWh por panel
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 font-medium">
                          Ahorro Mensual Estimado:
                        </span>
                        <div className="font-bold text-blue-700 text-base">
                          {formatQTZ(
                            COSTO_ENERGIA_PROM_SINPANEL *
                            ((KWH_MES_PANEL * selectedRegion?.porcentaje) /
                              100)
                          )}{" "}
                          por panel
                        </div>
                      </div>
                    </div>
                    {/* Usando CustomButton para el botón de navegación */}
                    <Link to="/calculadora">
                      <CustomButton
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        icon={ArrowRight}
                        iconPosition="right"
                        onClick={() => {
                          logEvent(
                            "Calculadora Solar",
                            "clic_calcular_region",
                            `Región: ${selectedRegion?.label}`
                          );
                        }}
                      >
                        Calcular para {selectedRegion?.label}
                      </CustomButton>
                    </Link>
                  </div>
                )}

                {/* Default Info */}
                {!selectedRegion && (
                  <div className="mt-6 text-center text-gray-500 p-4 border border-dashed border-gray-300 rounded-lg">
                    <MapPin
                      size={24}
                      className="mx-auto mb-3 opacity-60 text-gray-400"
                    />
                    <p className="text-base font-medium">
                      Haz clic en cualquier región en el mapa para ver su
                      potencial solar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
